import React from 'react';
import { Link } from 'react-router-dom';

const TERRACOTTA = '#BC6C25';
const DEEP_MOSS  = '#283618';
const PAPER      = '#FCF9F2';

const PrivacyPolicy = () => (
  <div style={pageStyle}>
    <div style={containerStyle}>

      {/* Header */}
      <div style={headerStyle}>

        {/* Logo */}
        <Link to="/" style={logoLinkStyle} aria-label="SayCiao.school — home">
        <svg width="34" height="34" viewBox="0 0 64 64" fill="none" style={{ flexShrink: 0 }}>            <rect width="64" height="64" rx="14" fill="#F7F4EF" stroke="rgba(0,0,0,0.04)" strokeWidth="1"/>
            <clipPath id="flag-pp"><rect width="64" height="64" rx="14"/></clipPath>
            <rect x="0"    y="0" width="21.3" height="64" fill="#009246" opacity="0.18" clipPath="url(#flag-pp)"/>
            <rect x="42.6" y="0" width="21.4" height="64" fill="#CE2B37" opacity="0.28" clipPath="url(#flag-pp)"/>
            <rect x="10" y="32" width="6" height="6"  rx="3" fill="#BC6C25" opacity="0.45"/>
            <rect x="19" y="26" width="6" height="18" rx="3" fill="#BC6C25" opacity="0.7"/>
            <rect x="28" y="14" width="6" height="42" rx="3" fill="#BC6C25"/>
            <rect x="37" y="20" width="6" height="30" rx="3" fill="#BC6C25" opacity="0.7"/>
            <rect x="46" y="28" width="6" height="14" rx="3" fill="#BC6C25" opacity="0.45"/>
          </svg>
          <span style={logoWordmarkStyle}>
            <span style={logoSayStyle}>say</span>
            <span style={logoCiaoStyle}>ciao</span>
            <span style={logoDotStyle}>.</span>
            <span style={logoSchoolStyle}>school</span>
          </span>
        </Link>

        <div style={dividerStyle} />

        <p style={kickerStyle}>Privacy Policy</p>
        <h1 style={titleStyle}>How I handle your data</h1>
        <p style={subtitleStyle}>
          Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
        <p style={introStyle}>
          This policy explains what information SayCiao.school collects, why, and how it is used.
          It is written in plain English — no legal jargon. If you have any questions, email me at{' '}
          <a href="mailto:sayciao.school@outlook.com" style={linkStyle}>sayciao.school@outlook.com</a>.
        </p>
      </div>

      {/* Sections */}
      <div style={sectionsStyle}>

        <Section title="1. Who I am">
          SayCiao.school is a one-on-one Italian tutoring service run by me, Francesca Muntoni,
          based in the United States. I offer personalised online Italian lessons to English-speaking
          adults worldwide via my website at sayciao.school.
        </Section>

        <Section title="2. What information I collect">
          <strong>When you contact me</strong> (via the contact form or email): your name and email address.
          <br /><br />
          <strong>When you book a lesson</strong> (via Calendly): your name, email, and any details you
          provide in the booking form. Calendly has its own privacy policy at{' '}
          <a href="https://calendly.com/privacy" target="_blank" rel="noreferrer" style={linkStyle}>calendly.com/privacy</a>.
          <br /><br />
          <strong>When you use the student portal</strong>: your name, email address, lesson history,
          vocabulary notes, and any resources I upload for you. This data is stored securely in Supabase
          (see Section 5).
          <br /><br />
          <strong>I do not collect</strong>: payment card details (payments are handled directly via
          Wise or Venmo), government IDs, or any sensitive personal data.
        </Section>

        <Section title="3. Why I collect it">
          I collect your information only for the following purposes:
          <br /><br />
          — To respond to your enquiries and communicate about lessons<br />
          — To schedule, confirm, and manage your lessons<br />
          — To maintain your student portal (lesson history, vocabulary, resources)<br />
          — To send you lesson-related notifications (reschedule confirmations, new materials)
          <br /><br />
          I will never sell your data, share it with advertisers, or use it for any purpose
          other than running your lessons.
        </Section>

        <Section title="4. Cookies">
          My website itself does not set cookies. However, two third-party services I use do:
          <br /><br />
          — <strong>Calendly</strong> sets cookies when the booking widget loads on my page.
          These are functional cookies used to display the booking calendar.<br />
          — <strong>Formspree</strong> may set a session cookie when you submit the contact form.
          <br /><br />
          You can disable cookies in your browser settings. This will not affect your ability
          to use the student portal.
        </Section>

        <Section title="5. How I store your data">
          Student portal data (lessons, vocabulary, resources) is stored in Supabase, a
          US-based database service with industry-standard encryption at rest and in transit.
          Supabase's privacy policy is available at{' '}
          <a href="https://supabase.com/privacy" target="_blank" rel="noreferrer" style={linkStyle}>supabase.com/privacy</a>.
          <br /><br />
          Resource files (homework, lesson materials) are stored in a private Supabase storage
          bucket and are accessible only to you and me. Files are automatically removed after 7 days.
          <br /><br />
          Your data is never stored on my local devices and is never transferred to countries
          without adequate data protection laws.
        </Section>

        <Section title="6. How long I keep your data">
          I keep your student portal data (lesson history, vocabulary) for as long as you are
          an active student. If you stop taking lessons, your data is retained for up to 12 months
          in case you return, then deleted.
          <br /><br />
          Contact form submissions are retained only as long as needed to respond to your enquiry.
          <br /><br />
          You can request deletion of your data at any time — see Section 8.
        </Section>

        <Section title="7. Your rights">
          Regardless of where you are located, you have the right to:
          <br /><br />
          — <strong>Access</strong> the personal data I hold about you<br />
          — <strong>Correct</strong> any inaccurate information<br />
          — <strong>Delete</strong> your account and all associated data<br />
          — <strong>Export</strong> your data in a portable format<br />
          — <strong>Opt out</strong> of any communications at any time
          <br /><br />
          <strong>California residents (CCPA)</strong>: You have the right to know what personal
          information is collected, the right to delete it, and the right to opt out of its sale
          (I do not sell personal information).
          <br /><br />
          <strong>EU/UK residents (GDPR)</strong>: My legal basis for processing your data is
          contractual necessity (to provide the tutoring service you have engaged me for) and
          legitimate interest (to communicate with you about your lessons).
        </Section>

        <Section title="8. How to contact me">
          For any privacy-related requests — including data access, correction, or deletion —
          please email me at{' '}
          <a href="mailto:sayciao.school@outlook.com" style={linkStyle}>sayciao.school@outlook.com</a>.
          <br /><br />
          I will respond within 5 business days. Deletion requests will be completed within 30 days.
        </Section>

        <Section title="9. Changes to this policy">
          I may update this policy occasionally. When I do, I will update the "last updated"
          date at the top of this page. I will notify active students of any significant changes
          via email.
        </Section>

      </div>

      {/* Footer note */}
      <div style={footNoteStyle}>
        <p style={footNoteText}>
          Questions? Email me at{' '}
          <a href="mailto:sayciao.school@outlook.com" style={linkStyle}>sayciao.school@outlook.com</a>
          {' '}— I'm happy to help.
        </p>
      </div>

    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={sectionStyle}>
    <h2 style={sectionTitleStyle}>{title}</h2>
    <p style={sectionBodyStyle}>{children}</p>
  </div>
);

// ─── Styles ───────────────────────────────────────────────────────────────────
const pageStyle = {
  backgroundColor: PAPER,
  minHeight: '100vh',
  padding: '60px 5% 80px',
  fontFamily: "'Montserrat', sans-serif",
};

const containerStyle = {
  maxWidth: '680px',
  margin: '0 auto',
};

const headerStyle = {
  marginBottom: '60px',
  paddingBottom: '40px',
  borderBottom: '1px solid rgba(40,54,24,0.08)',
};

// Logo — wave mark + wordmark style 6 (matches Navbar + Footer)
const logoLinkStyle     = { display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', marginBottom: '32px' };
const logoWordmarkStyle = { display: 'flex', alignItems: 'baseline', gap: 0 };
const logoSayStyle      = { fontFamily: "'Montserrat', sans-serif", fontSize: '1rem', fontWeight: 600, letterSpacing: '1px', color: DEEP_MOSS };
const logoCiaoStyle     = { fontFamily: "'Montserrat', sans-serif", fontSize: '1rem', fontWeight: 600, letterSpacing: '1px', color: TERRACOTTA };
const logoDotStyle      = { fontFamily: "'Montserrat', sans-serif", fontSize: '1rem', fontWeight: 300, color: TERRACOTTA, opacity: 0.5 };
const logoSchoolStyle   = { fontFamily: "'Montserrat', sans-serif", fontSize: '1rem', fontWeight: 300, letterSpacing: '1px', color: DEEP_MOSS, opacity: 0.65 };
const dividerStyle = {
  width: '32px',
  height: '1px',
  backgroundColor: TERRACOTTA,
  opacity: 0.5,
  margin: '0 0 24px',
};

const kickerStyle = {
  fontSize: '0.65rem',
  letterSpacing: '3px',
  color: TERRACOTTA,
  fontWeight: 700,
  margin: '0 0 10px',
  textTransform: 'uppercase',
};

const titleStyle = {
  fontFamily: "'Cormorant Garamond', serif",
  fontSize: 'clamp(2rem, 5vw, 2.8rem)',
  fontWeight: 400,
  color: DEEP_MOSS,
  margin: '0 0 16px',
  lineHeight: 1.1,
};

const subtitleStyle = {
  fontSize: '0.72rem',
  color: DEEP_MOSS,
  opacity: 0.45,
  margin: '0 0 20px',
  letterSpacing: '0.3px',
};

const introStyle = {
  fontSize: '0.85rem',
  color: DEEP_MOSS,
  lineHeight: '1.8',
  margin: 0,
  opacity: 0.72,
};

const sectionsStyle = {
  display: 'flex',
  flexDirection: 'column',
};

const sectionStyle = {
  padding: '32px 0',
  borderBottom: '1px solid rgba(40,54,24,0.06)',
};

const sectionTitleStyle = {
  fontSize: '0.65rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  color: TERRACOTTA,
  margin: '0 0 14px',
};

const sectionBodyStyle = {
  fontSize: '0.83rem',
  color: DEEP_MOSS,
  lineHeight: '1.9',
  margin: 0,
  opacity: 0.78,
};

const linkStyle = {
  color: TERRACOTTA,
  textDecoration: 'none',
  borderBottom: '1px solid rgba(188,108,37,0.3)',
  paddingBottom: '1px',
};

const footNoteStyle = {
  marginTop: '48px',
  padding: '20px 24px',
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid rgba(188,108,37,0.1)',
};

const footNoteText = {
  fontSize: '0.8rem',
  color: DEEP_MOSS,
  opacity: 0.65,
  margin: 0,
  lineHeight: '1.6',
};

export default PrivacyPolicy;